import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

/**
 * v43.8 (Mar 2026) — Admin Customer email order-intake configuration.
 *
 * Standalone page under /admin/customers/:customer_id/email-intake.
 * Reuses backend routes /api/admin/customers/{id}/email-intake (v43.7).
 * Admin-only. Customer role cannot access this route (backend enforced).
 */

export default function AdminCustomerEmailIntakePage() {
  const { customer_id } = useParams();
  const { apiGet, apiPost, apiDelete, apiPatch, token } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [senderInput, setSenderInput] = useState("");
  const [senderErr, setSenderErr] = useState(null);

  // useAuth() does not expose apiPut; call fetch directly with the token.
  const apiPut = async (path, body) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json",
                 ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw { status: r.status, data: j };
    return j;
  };

  const load = useCallback(async () => {
    try { setData(await apiGet(`/api/admin/customers/${customer_id}/email-intake`)); }
    catch (e) { setErr(e?.data?.detail || "Errore caricamento"); }
  }, [apiGet, customer_id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ padding: 24, color: "#991b1b" }}>{typeof err === "string" ? err : JSON.stringify(err)}</div>;
  if (!data) return <p style={{ padding: 24 }}>Caricamento…</p>;

  const settings = data.settings || {};
  const enabled = !!settings.email_intake_enabled;

  const toggle = async (newEnabled) => {
    if (!newEnabled && enabled) {
      if (!window.confirm("Disabilitando questa funzione, le nuove email non verranno più importate automaticamente per questo Customer. Gli import già esistenti resteranno disponibili. Procedere?")) return;
    }
    setSaving(true);
    try {
      await apiPut(`/api/admin/customers/${customer_id}/email-intake`, {
        email_intake_enabled: newEnabled,
        email_intake_mode: newEnabled ? "OFFICE_REVIEW" : "OFF",
      });
      await load();
    } catch (e) {
      const d = e?.data?.detail;
      const msg = (typeof d === "string" && d)
        || d?.message
        || "Errore salvataggio.";
      alert(msg);
    } finally { setSaving(false); }
  };

  const addSender = async () => {
    setSenderErr(null);
    const email = (senderInput || "").trim().toLowerCase();
    if (!email) { setSenderErr("Inserisci un indirizzo email."); return; }
    try {
      await apiPost(`/api/admin/customers/${customer_id}/email-intake/senders`, { email });
      setSenderInput("");
      await load();
    } catch (e) {
      const d = e?.data?.detail;
      if (d === "invalid_email") setSenderErr("Indirizzo email non valido.");
      else setSenderErr(
        (typeof d === "string" && d)
        || d?.message
        || "Errore."
      );
    }
  };

  const patchSender = async (sid, active) => {
    try { await apiPatch(`/api/admin/customers/${customer_id}/email-intake/senders/${sid}`, { active }); await load(); }
    catch (e) {
      const d = e?.data?.detail;
      alert((typeof d === "string" && d) || d?.message || "Errore.");
    }
  };
  const removeSender = async (sid) => {
    if (!window.confirm("Rimuovere questo indirizzo email dagli autorizzati?")) return;
    try { await apiDelete(`/api/admin/customers/${customer_id}/email-intake/senders/${sid}`); await load(); }
    catch (e) {
      const d = e?.data?.detail;
      alert((typeof d === "string" && d) || d?.message || "Errore.");
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}
         data-testid="admin-customer-email-intake">
      <div style={{ marginBottom: 20 }}>
        <Link to="/admin" style={{ color: "#0F4C81", fontSize: 13 }}>← Admin</Link>
        <h1 style={{ fontSize: 26, margin: "8px 0" }}>Canali ordine — email bypass</h1>
        <p style={{ color: "#666", fontSize: 14, margin: 0 }}>
          Customer id: <code>{customer_id}</code>
        </p>
        <p style={{ color: "#374151", fontSize: 13, margin: "6px 0 0" }}>
          Canali standard: <strong>Portale</strong> + <strong>Caricamento documento</strong>.
          Il canale email è un permesso eccezionale a livello Customer.
        </p>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: 20,
                    boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input data-testid="email-intake-toggle" type="checkbox"
                 checked={enabled} disabled={saving}
                 onChange={(e) => toggle(e.target.checked)}
                 style={{ transform: "scale(1.4)" }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              Consenti ordini via email
            </div>
            <div style={{ color: "#666", fontSize: 13 }}>
              Autorizza questo Customer a bypassare l'inserimento ordine dal
              portale. Solo email provenienti dagli indirizzi autorizzati
              verranno considerate un canale ordine valido.
            </div>
          </div>
        </div>
        {enabled && (
          <div data-testid="email-intake-mode-info"
               style={{ marginTop: 10, padding: 10, background: "#f0f9ff",
                        border: "1px solid #bae6fd", borderRadius: 8,
                        fontSize: 13, color: "#075985" }}>
            Le email provenienti dagli indirizzi autorizzati vengono
            analizzate e trasformate in una bozza d'ordine visibile in
            Import ordini per verifica. Nessun ordine viene confermato
            automaticamente: un operatore dell'ufficio deve rivedere le
            righe e confermare l'import prima che diventi un ordine reale.
            Le email da mittenti non autorizzati vengono ignorate.
          </div>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: 20,
                    boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
        <h2 style={{ fontSize: 18, margin: "0 0 12px" }}>Indirizzi email autorizzati</h2>
        <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>
          Solo email inviate da questi indirizzi verranno considerate ordini
          validi per questo Customer. Il confronto è case-insensitive.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input data-testid="email-sender-input" type="email"
                 placeholder="ordini@cliente.it"
                 value={senderInput}
                 onChange={(e) => setSenderInput(e.target.value)}
                 style={{ flex: 1, padding: 10, borderRadius: 8,
                          border: "1px solid #d1d5db", fontSize: 14 }} />
          <button data-testid="email-sender-add-btn"
                  onClick={addSender}
                  style={{ padding: "10px 20px", background: "#0F4C81",
                           color: "#fff", border: 0, borderRadius: 8,
                           fontWeight: 600, cursor: "pointer" }}>
            AGGIUNGI
          </button>
        </div>
        {senderErr && (
          <div data-testid="email-sender-error"
               style={{ padding: 8, background: "#fef2f2", color: "#991b1b",
                        borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            {senderErr}
          </div>
        )}

        {(!data.senders || data.senders.length === 0) && (
          <p style={{ color: "#888", fontSize: 14 }}>
            Nessun indirizzo email autorizzato.
          </p>
        )}
        {data.senders && data.senders.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f6f8fb", textAlign: "left" }}>
                <th style={{ padding: "8px 10px", fontSize: 12, color: "#555" }}>Email</th>
                <th style={{ padding: "8px 10px", fontSize: 12, color: "#555" }}>Attivo</th>
                <th style={{ padding: "8px 10px", fontSize: 12, color: "#555" }}>Aggiunto</th>
                <th style={{ padding: "8px 10px" }}></th>
              </tr>
            </thead>
            <tbody>
              {data.senders.map(s => (
                <tr key={s.id} data-testid={`email-sender-row-${s.id}`}
                    style={{ borderTop: "1px solid #eef1f5" }}>
                  <td style={{ padding: "8px 10px" }}><code>{s.email}</code></td>
                  <td style={{ padding: "8px 10px" }}>
                    <input type="checkbox" checked={!!s.active}
                           data-testid={`email-sender-active-${s.id}`}
                           onChange={(e) => patchSender(s.id, e.target.checked)} />
                  </td>
                  <td style={{ padding: "8px 10px", color: "#666", fontSize: 12 }}>
                    {s.created_at ? new Date(s.created_at).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    <button data-testid={`email-sender-remove-${s.id}`}
                            onClick={() => removeSender(s.id)}
                            style={{ padding: "4px 10px", background: "#fff",
                                     border: "1px solid #d1d5db", borderRadius: 6,
                                     color: "#991b1b", cursor: "pointer", fontSize: 12 }}>
                      Rimuovi
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
